import { useState } from "react";
import type { MouseEvent } from "react";
import { ArrowRight, Lock } from "lucide-react";
import { TIER_NAMES } from "../components/TierLockModal";
import { useExperience } from "../context/ExperienceContext";
import { useNavModel } from "../nav/areas";
import { isPlainLeftClick } from "../nav/LinkAdapter";
import type { NavCatalogueEntry } from "../nav/types";
import { card, emptyFrame, theme, type as text, pageHeading, pageSubtitle } from "../theme";

// ── More tools ────────────────────────────────────────────────────────────
// Every page the sidebar leaves out (nav/areas.ts): modules that do not apply
// yet or that Guided mode keeps out of the way, and features in a higher
// service tier. One row in the sidebar instead of a row - or a padlock - each.

function groupByArea(entries: NavCatalogueEntry[]): Array<[string, NavCatalogueEntry[]]> {
  const groups = new Map<string, NavCatalogueEntry[]>();
  for (const entry of entries) {
    const key = entry.area ?? "Other";
    groups.set(key, [...(groups.get(key) ?? []), entry]);
  }
  return Array.from(groups.entries());
}

function ToolRow({ entry, onOpen, locked }: { entry: NavCatalogueEntry; onOpen: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void; locked: boolean }) {
  const { adapter } = useNavModel();
  const [hov, setHov] = useState(false);
  const { item } = entry;
  const tierName = item.lock ? (TIER_NAMES[item.lock.minTier] ?? `tier ${item.lock.minTier}`) : null;
  const body = (
    <>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={text.itemTitle}>{item.label}</div>
        {item.description && <div style={{ ...text.hint, marginTop: 2 }}>{item.description}</div>}
        {locked && tierName && <div style={{ ...text.meta, marginTop: 3 }}>Included from {tierName}</div>}
      </div>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: text.hint.fontSize, fontWeight: 600, color: locked ? theme.color.muted : theme.color.accentText, flexShrink: 0 }}>
        {locked ? <><Lock size={12} /> Unlock</> : <>Open <ArrowRight size={13} /></>}
      </span>
    </>
  );
  const style = {
    display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "12px 16px",
    background: hov ? "var(--c-panelAlt)" : "transparent", border: "none", borderTop: "1px solid var(--c-border)",
    textAlign: "left" as const, cursor: "pointer", textDecoration: "none", fontFamily: theme.font,
  };
  if (locked || !item.to || !adapter) {
    return <button type="button" onClick={onOpen} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={style}>{body}</button>;
  }
  return <a href={adapter.href(item.to)} onClick={onOpen} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={style}>{body}</a>;
}

export function MoreToolsPage() {
  const { model, adapter, onLocked } = useNavModel();
  const { novice } = useExperience();
  const deferred = model?.catalogue?.deferred ?? [];
  const locked = model?.catalogue?.locked ?? [];

  const open = (entry: NavCatalogueEntry) => (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (entry.item.lock) { onLocked(entry.item); return; }
    // A modified click on a link opens it in a new tab, as any link does.
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    if (entry.item.to && adapter) adapter.navigate(entry.item.to);
  };

  return (
    <div style={{ padding: "20px 24px" }}>
      <h1 style={pageHeading}>More tools</h1>
      <p style={{ ...pageSubtitle, margin: "0 0 20px" }}>
        The menu shows what you use. Everything else is here, ready when you need it. A tool moves into the menu by itself once you start using it.
        {novice && " Switch to Expert mode (bottom of the menu) to see every page there."}
      </p>

      {deferred.length === 0 && locked.length === 0 && (
        <div style={{ ...emptyFrame, ...text.body, color: theme.color.subtle }}>Every tool is already in your menu.</div>
      )}

      {groupByArea(deferred).map(([area, entries]) => (
        <section key={area} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
          <h2 style={{ ...text.eyebrow, color: theme.color.faint, margin: 0, padding: "12px 16px 10px" }}>{area}</h2>
          {entries.map((entry) => <ToolRow key={entry.item.id} entry={entry} locked={false} onOpen={open(entry)} />)}
        </section>
      ))}

      {locked.length > 0 && (
        <section style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
          <h2 style={{ ...text.eyebrow, color: theme.color.faint, margin: 0, padding: "12px 16px 10px" }}>In a higher plan</h2>
          {locked.map((entry) => <ToolRow key={entry.item.id} entry={entry} locked onOpen={open(entry)} />)}
        </section>
      )}
    </div>
  );
}
